/**
 * レセプト内容一覧 (Receipt Content List) — list page.
 * URL: /{tenant}/rezept_histories/init
 *
 * Search conditions and pagination are mirrored to URL query params for bookmarking.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AppButton } from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import useAppToast from '@/hooks/useAppToast';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';

import { useExportRezeptHistoriesCsvMutation } from '@/hooks/main/useExportRezeptHistoriesCsvMutation';
import { useRezeptHistoriesInit } from '@/hooks/main/useRezeptHistoriesInit';
import { useRezeptHistoriesSearch } from '@/hooks/main/useRezeptHistoriesSearch';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { getRezeptHistoriesAllIds } from '@/services/main/rezeptHistories';

import RezeptHistoriesSearchForm, {
  VISITMONTH_FORMAT_MESSAGE,
  YEARMONTH_FORMAT_MESSAGE,
  YEARMONTH_REQUIRED_MESSAGE,
} from '../components/RezeptHistoriesSearchForm';
import RezeptHistoriesTable from '../components/RezeptHistoriesTable';
import {
  LIST_PAGE_SIZE,
  NO_SELECTION_MESSAGE,
  REZEPT_KBN_MAIN,
  SEARCH_RADIO_INSURER_TYPE,
  SORT_BY_SERIAL,
} from '../constants';
import type { RezeptHistoriesFormValues, RezeptHistoriesSearchPayload } from '../types';
import {
  formValuesToSearchParams,
  formValuesToSearchPayload,
  searchParamsToFormValues,
  searchParamsToPage,
} from '../urlParams';

const FALLBACK_DEFAULTS: RezeptHistoriesFormValues = {
  search_yearmonth: '',
  search_visitmonth: '',
  massager_id: null,
  search_radio: SEARCH_RADIO_INSURER_TYPE,
  insurer_type_id: null,
  ini: '',
  insurer_name: '',
  rezept_kbn: REZEPT_KBN_MAIN,
  sort_radio: SORT_BY_SERIAL,
};

const RezeptHistoriesListPage: React.FC = () => {
  const toast = useAppToast();
  const sidebarOpen = useSidebarOpen();
  const { serviceType } = useTheme();
  const { tenant, clinicNumber } = useClinicContext();

  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const { data: initData } = useRezeptHistoriesInit();

  // Defaults from BE init endpoint (current month, etc.)
  const serverDefaults = useMemo<RezeptHistoriesFormValues>(() => {
    const d = initData?.defaults;
    if (!d) return FALLBACK_DEFAULTS;
    return {
      search_yearmonth: d.search_yearmonth ?? '',
      search_visitmonth: d.search_visitmonth ?? '',
      massager_id: d.massager_id ?? null,
      search_radio: d.search_radio ?? SEARCH_RADIO_INSURER_TYPE,
      insurer_type_id: d.insurer_type_id ?? null,
      ini: d.ini ?? '',
      insurer_name: d.insurer_name ?? '',
      rezept_kbn: d.rezept_kbn ?? REZEPT_KBN_MAIN,
      sort_radio: d.sort_radio ?? SORT_BY_SERIAL,
    };
  }, [initData?.defaults]);

  // Form initial values: hydrate from URL if query present, else use server defaults.
  // Re-computes when server defaults arrive (first init load) — merged with URL overrides.
  const initialFormValues = useMemo<RezeptHistoriesFormValues>(() => {
    const fromUrl = searchParamsToFormValues(urlSearchParams, serverDefaults);
    return fromUrl ?? serverDefaults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDefaults]);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [currentFormValues, setCurrentFormValues] = useState<RezeptHistoriesFormValues | null>(
    null
  );
  const [activePayload, setActivePayload] = useState<RezeptHistoriesSearchPayload | null>(null);

  // On first mount, if URL already has filter params → kick off a search automatically.
  // Once hydration runs (or is determined unnecessary), the ref stops this from
  // re-firing when the user triggers a search and the URL changes.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    // Wait for server defaults to settle so yearmonth defaulting is consistent.
    if (!serverDefaults.search_yearmonth && FALLBACK_DEFAULTS === serverDefaults) return;
    const fromUrl = searchParamsToFormValues(urlSearchParams, serverDefaults);
    hydratedRef.current = true;
    if (!fromUrl || !fromUrl.search_yearmonth) return;
    const page = searchParamsToPage(urlSearchParams);
    setCurrentFormValues(fromUrl);
    setActivePayload(formValuesToSearchPayload(fromUrl, page, LIST_PAGE_SIZE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDefaults]);

  const { data: searchResult, isLoading: isSearching } = useRezeptHistoriesSearch(activePayload);

  const rows = searchResult?.data ?? [];
  const pagination = searchResult?.pagination ?? null;
  const totalCount = pagination?.total ?? 0;

  // Reset state when service-type actually changes (A2 ↔ R2).
  // Compare against the previous value (not a mount flag) so React StrictMode's
  // effect replay in dev doesn't incorrectly fire the reset and wipe URL params.
  // NOTE: `setUrlSearchParams` identity changes on every URL change, so we
  // intentionally exclude it from deps to avoid the effect firing on every search.
  const lastServiceTypeRef = useRef(serviceType);
  useEffect(() => {
    if (lastServiceTypeRef.current === serviceType) return;
    lastServiceTypeRef.current = serviceType;
    setSelectedIds([]);
    setCurrentFormValues(null);
    setActivePayload(null);
    setUrlSearchParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  const massagerLabel =
    serviceType?.toUpperCase() === SERVICE_TYPE_R2 ? 'マッサージ師' : 'はり・きゅう師';

  const handleSearch = useCallback(
    (form: RezeptHistoriesFormValues) => {
      setCurrentFormValues(form);
      setSelectedIds([]);
      setActivePayload(formValuesToSearchPayload(form, 1, LIST_PAGE_SIZE));
      setUrlSearchParams(formValuesToSearchParams(form, 1), { replace: true });
    },
    [setUrlSearchParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!currentFormValues) return;
      setActivePayload(formValuesToSearchPayload(currentFormValues, page, LIST_PAGE_SIZE));
      // Flip only the `page` key; keep filter params intact.
      setUrlSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('page', String(page));
          return next;
        },
        { replace: true }
      );
    },
    [currentFormValues, setUrlSearchParams]
  );

  const handleEmptyYearmonth = useCallback(() => {
    toast.error(YEARMONTH_REQUIRED_MESSAGE);
  }, [toast]);

  const handleInvalidYearmonth = useCallback(() => {
    toast.error(YEARMONTH_FORMAT_MESSAGE);
  }, [toast]);

  const handleInvalidVisitmonth = useCallback(() => {
    toast.error(VISITMONTH_FORMAT_MESSAGE);
  }, [toast]);

  // 全選択 header checkbox (BD FUNC_A.15_1/2):
  //   tick   → fetch all matching IDs from BE, set as full selection (cross-page)
  //   untick → clear ALL selection across all pages (symmetric with tick)
  const handleSelectAll = useCallback(
    async (selected: boolean) => {
      if (!selected) {
        setSelectedIds([]);
        return;
      }
      if (!activePayload) return;
      try {
        const ids = await getRezeptHistoriesAllIds(
          tenant,
          clinicNumber,
          activePayload,
          serviceType
        );
        setSelectedIds(ids);
      } catch {
        toast.error('全選択に失敗しました');
      }
    },
    [activePayload, tenant, clinicNumber, serviceType, toast]
  );

  const { triggerExport, isLoading: isExporting } = useExportRezeptHistoriesCsvMutation();

  const handleExportCsv = useCallback(() => {
    if (selectedIds.length === 0) {
      toast.error(NO_SELECTION_MESSAGE);
      return;
    }
    if (!currentFormValues) return;

    void triggerExport({
      history_ids: selectedIds,
      rezept_kbn: currentFormValues.rezept_kbn,
      sort_radio: currentFormValues.sort_radio,
    });
  }, [selectedIds, currentFormValues, toast, triggerExport]);

  const totalCountLabel = `該当件数: ${totalCount} 件`;

  return (
    <>
      <div className="pb-4 space-y-4">
        <CollapsibleCard title="レセプト検索">
          <RezeptHistoriesSearchForm
            key={serviceType}
            initialValues={initialFormValues}
            insurerTypes={initData?.insurer_types ?? []}
            iniOptions={initData?.ini_options ?? []}
            massagerLabel={massagerLabel}
            totalCount={totalCount}
            isSearching={isSearching}
            onSearch={handleSearch}
            onEmptyYearmonth={handleEmptyYearmonth}
            onInvalidYearmonth={handleInvalidYearmonth}
            onInvalidVisitmonth={handleInvalidVisitmonth}
          />
        </CollapsibleCard>

        {activePayload !== null && (
          <CollapsibleCard>
            <div className="mb-2 text-sm text-gray-700">{totalCountLabel}</div>
            <RezeptHistoriesTable
              data={rows}
              pagination={pagination}
              selectedIds={selectedIds}
              onSelectChange={setSelectedIds}
              onSelectAll={handleSelectAll}
              onPageChange={handlePageChange}
              isLoading={isSearching}
              hasFilters
            />
          </CollapsibleCard>
        )}
      </div>

      <BottomBar
        variant="action"
        visible={rows.length > 0}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <AppButton
            type="primary"
            className="rounded"
            onClick={handleExportCsv}
            loading={isExporting}
          >
            CSVデータ出力
          </AppButton>
        }
      />
    </>
  );
};

export default RezeptHistoriesListPage;
